<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue'
import { doc, runTransaction } from 'firebase/firestore'
import { db } from '@/firebase'
import { useIncidentStore } from '@/stores/incident'
import { usePermissionsStore } from '@/stores/permissions'
import { useAuthStore } from '@/stores/auth'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { useToastStore } from '@/stores/toast'
import { IncidentStatus } from '@/types'
import type { MissionType } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosDateInput from '@/components/ui/FosDateInput.vue'
import SecondaryLogoUpload from '@/components/incident/SecondaryLogoUpload.vue'

const incidentId = inject<string>('incidentId')!
const incidentStore = useIncidentStore()
const permissionsStore = usePermissionsStore()
const authStore = useAuthStore()
const { opRole } = useCurrentOpRole()
const toast = useToastStore()

const incident = computed(() => incidentStore.currentIncident)
const isClosed = computed(() => incident.value?.status === IncidentStatus.Closed)
const canClose = computed(() => permissionsStore.can('incident.create_close', opRole.value))
const canEdit = computed(() => permissionsStore.can('incident.edit', opRole.value) && !isClosed.value)

const localName = ref('')
const localNumber = ref('')
const localStartDate = ref('')
const localStartTime = ref('')
const localRequestingAgency = ref('')
const localIcpLocation = ref('')
const localContactPhone = ref('')

watch(incident, (inc) => {
  if (!inc) return
  localName.value = inc.incident_name ?? ''
  localNumber.value = inc.incident_number ?? ''
  localStartDate.value = inc.start_date ?? ''
  localStartTime.value = inc.start_time ?? ''
  localRequestingAgency.value = inc.requesting_agency_name ?? ''
  localIcpLocation.value = inc.icp_location ?? ''
  localContactPhone.value = inc.contact_phone ?? ''
}, { immediate: true })

async function saveField(field: string, value: string | null) {
  if (!canEdit.value) return
  try {
    await incidentStore.updateIncident(incidentId, { [field]: value || null })
  } catch {
    toast.add('Failed to save', 'error')
  }
}

const missionTypeOptions = [
  { label: 'Lost Person', value: 'lost_person' },
  { label: 'Rescue', value: 'rescue' },
  { label: 'Recovery', value: 'recovery' },
  { label: 'Other', value: 'other' },
]

async function saveMissionType(value: string) {
  if (!canEdit.value) return
  try {
    await incidentStore.updateIncident(incidentId, { mission_type: (value as MissionType) || null })
  } catch {
    toast.add('Failed to save', 'error')
  }
}

const generatingNumber = ref(false)
async function generateNumber() {
  const agencyId = authStore.userProfile?.agency_id
  if (!agencyId) return
  generatingNumber.value = true
  try {
    const agencyRef = doc(db, 'agencies', agencyId)
    let newNumber = 1
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(agencyRef)
      const last = snap.exists() ? (snap.data().last_incident_number ?? 0) : 0
      newNumber = (last as number) + 1
      tx.update(agencyRef, { last_incident_number: newNumber })
    })
    const year = new Date().getFullYear().toString().slice(-2)
    const padded = String(newNumber).padStart(3, '0')
    const generated = `${year}-${padded}`
    localNumber.value = generated
    await incidentStore.updateIncident(incidentId, { incident_number: generated })
  } catch {
    toast.add('Failed to generate number', 'error')
  } finally {
    generatingNumber.value = false
  }
}

const outcomeOptions = [
  { label: 'Subject Found', value: 'found' },
  { label: 'Not Found', value: 'not_found' },
  { label: 'Suspended', value: 'suspended' },
]

async function saveOutcome(value: string | null) {
  if (!isClosed.value) return
  try {
    await incidentStore.updateIncident(incidentId, { outcome: value as 'found' | 'not_found' | 'suspended' | null })
    toast.add('Outcome updated', 'success')
  } catch {
    toast.add('Failed to save outcome', 'error')
  }
}

const showCloseDialog = ref(false)
const selectedOutcome = ref<string | null>(null)
const closing = ref(false)

function openCloseDialog() {
  selectedOutcome.value = incident.value?.outcome ?? null
  showCloseDialog.value = true
}

async function confirmClose() {
  if (!selectedOutcome.value) {
    toast.add('Please select an outcome before closing.', 'warning')
    return
  }
  closing.value = true
  try {
    await incidentStore.closeIncident(incidentId, selectedOutcome.value as 'found' | 'not_found' | 'suspended')
    showCloseDialog.value = false
    toast.add('Incident closed', 'success')
  } catch {
    toast.add('Failed to close incident', 'error')
  } finally {
    closing.value = false
  }
}
</script>

<template>
  <FosDialog
    :open="showCloseDialog"
    title="Close Incident"
    max-width="32rem"
    @update:open="showCloseDialog = $event"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-on-surface-variant">
        Before closing, please record the outcome of this incident. This cannot be undone.
      </p>
      <FosSelect
        :model-value="selectedOutcome ?? undefined"
        :options="outcomeOptions"
        placeholder="Select outcome (required)"
        @update:model-value="selectedOutcome = $event"
      />
    </div>
    <template #footer>
      <div class="flex items-center justify-end gap-2">
        <FosButton variant="tertiary" @click="showCloseDialog = false">Cancel</FosButton>
        <FosButton
          variant="primary"
          class="bg-error-container border-error-container text-on-surface"
          :disabled="!selectedOutcome || closing"
          @click="confirmClose"
        >
          <span v-if="closing" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin" />
          <span v-else>Close Incident</span>
        </FosButton>
      </div>
    </template>
  </FosDialog>

  <div class="h-full overflow-y-auto">
    <div class="max-w-2xl mx-auto px-4 py-6 space-y-6">

      <RouterLink
        :to="{ name: 'incident-settings', params: { id: incidentId } }"
        class="text-[10px] text-outline font-label uppercase tracking-widest hover:text-on-surface-variant transition-colors flex items-center gap-1"
      >
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        Back to Settings
      </RouterLink>

      <!-- Mission Details -->
      <div>
        <h3 class="text-[10px] font-label uppercase tracking-widest text-outline mb-3">Mission Details</h3>
        <div class="bg-surface-container border border-border p-5 space-y-4">

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-outline font-label uppercase tracking-widest">Mission Name</label>
              <FosInput
                v-model="localName"
                :disabled="!canEdit"
                @blur="saveField('incident_name', localName)"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-outline font-label uppercase tracking-widest">Mission Number</label>
              <div class="flex gap-2">
                <FosInput
                  v-model="localNumber"
                  class="flex-1"
                  :disabled="!canEdit"
                  @blur="saveField('incident_number', localNumber)"
                />
                <FosButton
                  v-if="canEdit"
                  variant="secondary"
                  size="sm"
                  :disabled="generatingNumber"
                  @click="generateNumber"
                >
                  {{ generatingNumber ? '...' : 'Auto' }}
                </FosButton>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-outline font-label uppercase tracking-widest">Mission Type</label>
              <FosSelect
                :model-value="incident?.mission_type ?? ''"
                :options="missionTypeOptions"
                :disabled="!canEdit"
                placeholder="Select type"
                @update:model-value="saveMissionType($event)"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-outline font-label uppercase tracking-widest">Start Date</label>
              <FosDateInput
                v-model="localStartDate"
                :disabled="!canEdit"
                @blur="saveField('start_date', localStartDate)"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-outline font-label uppercase tracking-widest">Start Time</label>
              <FosInput
                v-model="localStartTime"
                type="time"
                :disabled="!canEdit"
                @blur="saveField('start_time', localStartTime)"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-outline font-label uppercase tracking-widest">Requesting Agency</label>
              <FosInput
                v-model="localRequestingAgency"
                :disabled="!canEdit"
                @blur="saveField('requesting_agency_name', localRequestingAgency)"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-outline font-label uppercase tracking-widest">ICP Location</label>
              <FosInput
                v-model="localIcpLocation"
                :disabled="!canEdit"
                @blur="saveField('icp_location', localIcpLocation)"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] text-outline font-label uppercase tracking-widest">Contact Phone</label>
              <FosInput
                v-model="localContactPhone"
                type="tel"
                :disabled="!canEdit"
                @blur="saveField('contact_phone', localContactPhone)"
              />
            </div>
          </div>

        </div>
      </div>

      <!-- Administration -->
      <div>
        <h3 class="text-[10px] font-label uppercase tracking-widest text-outline mb-3">Administration</h3>

        <div class="bg-surface-container border border-border p-5 mb-3">
          <p class="text-[10px] font-label uppercase tracking-widest text-outline mb-1">Secondary Agency Logo</p>
          <p class="text-sm text-on-surface-variant mb-3">
            Displayed on the right side of the missing person flyer (LE or partner agency). Leave empty for single-agency operations.
          </p>
          <SecondaryLogoUpload
            :incident-id="incidentId"
            :logo-url="incident?.secondary_agency_logo ?? null"
            :can-edit="canEdit"
          />
        </div>

        <div class="bg-surface-container border border-border p-5 mb-3">
          <p class="text-[10px] font-label uppercase tracking-widest text-outline mb-3">Outcome</p>
          <template v-if="isClosed">
            <p class="text-sm text-on-surface-variant mb-3">Set the final outcome of this incident.</p>
            <FosSelect
              :model-value="incident?.outcome ?? ''"
              :options="outcomeOptions"
              :disabled="!permissionsStore.can('incident.edit', opRole)"
              placeholder="Select outcome"
              class="w-full sm:w-72"
              @update:model-value="saveOutcome($event || null)"
            />
          </template>
          <template v-else>
            <p class="text-sm text-on-surface-variant">Outcome can only be set after the incident is closed.</p>
          </template>
        </div>

        <div
          v-if="!isClosed && canClose"
          class="bg-surface-container border border-error/20 p-5"
        >
          <p class="text-[10px] font-label uppercase tracking-widest text-error mb-2">Close Incident</p>
          <p class="text-sm text-on-surface-variant mb-4">
            Closing an incident is permanent. All data will remain readable but no further changes can be made.
          </p>
          <FosButton variant="secondary" class="border-error/30 text-error" @click="openCloseDialog">
            Close Incident
          </FosButton>
        </div>

        <div v-if="isClosed" class="bg-surface-container border border-border p-5">
          <p class="text-sm text-on-surface-variant">This incident is closed. All data is read-only.</p>
        </div>
      </div>

    </div>
  </div>
</template>
